import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useGlobalStore } from '@cccom/config/global-store';
import { log } from '@cccom/shared/logging';
import { ControlledSelect } from '@cccom/ui/src/components/custom/react-select/controlled-select/ControlledSelect';
import { Button } from '@cccom/ui/src/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cccom/ui/src/components/shadcn/dialog';
import { Form } from '@cccom/ui/src/components/shadcn/form';

import {
  useCurrentSite,
  useSetCurrentSite,
  useSites,
} from '../data-access/sites.api';

type SiteFormValues = { siteId: string | null };

export function SiteSelectorModal() {
  const [t] = useTranslation();

  const setCurrentSite = useGlobalStore((state) => state.setCurrentSite);
  const setInitialized = useGlobalStore((state) => state.setIsInitialized);

  const { data: sites } = useSites();
  const { data: currentSite } = useCurrentSite();
  const { mutate: saveCurrentSite, isPending } = useSetCurrentSite();

  // Open the modal if the current site is auto-selected and there is more than one site available
  const hasMultipleSites = (sites?.length || 0) > 1;
  const isOpen = currentSite?.autoSelected === true && hasMultipleSites;
  const siteOptions = sites?.length
    ? sites.map((site) => ({ value: site.entityId, label: site.name }))
    : [];

  const formMethods = useForm<SiteFormValues>();
  const { control, handleSubmit, setValue } = formMethods;

  useEffect(() => {
    if (!currentSite) return;

    // If the modal is closed (site already selected), ensure the current site is set in the global store
    if (!isOpen) {
      setCurrentSite(currentSite.site);
      setInitialized(true);
    }

    if (currentSite.autoSelected) {
      // Pre-select the site in the dropdown
      setValue('siteId', currentSite.site.entityId);
    }
  }, [currentSite, isOpen, setInitialized, setCurrentSite, setValue]);

  const onSubmitHandler = async ({ siteId }: SiteFormValues) => {
    const site = sites?.find(({ entityId }) => entityId === siteId) ?? null;
    if (!siteId || !site) {
      toast.error(t('sites.select_site_required'), {
        position: 'top-center',
        duration: 1000,
      });
      return;
    }

    try {
      setCurrentSite(site);
      setInitialized(true);
      await saveCurrentSite(siteId);
    } catch (error) {
      toast.error(t('sites.select_site_error'), { position: 'top-center' });
      log.error('Failed to set site:', error);
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="border-t-brand-400 border-t-[6px]"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{t('sites.site_selection')}</DialogTitle>
        </DialogHeader>

        <Form {...formMethods}>
          <form onSubmit={handleSubmit(onSubmitHandler)}>
            <div className="flex flex-col gap-4 py-7">
              <DialogDescription>
                {t('sites.select_site_description')}
              </DialogDescription>

              <ControlledSelect
                name="siteId"
                control={control}
                options={siteOptions}
                styles={{ menu: (provided) => ({ ...provided, top: 'auto' }) }}
              />
            </div>

            <DialogFooter className="mt-7">
              <Button type="submit" disabled={isPending}>
                {t('common.actions.continue')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
